import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Loader2 } from "lucide-react";
import { useEndCall } from "@/lib/api/calls.ts";
import { StatefulRoom } from "@/lib/sendbird-calls";
import { useQueryClient } from "@tanstack/react-query";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

interface CallDisconnectConfirmationModalProps {
  room: StatefulRoom;
  callId: string;
  open: boolean;
  onClose: () => void;
}

export function CallDisconnectConfirmationModal({
  callId,
  open,
  room,
  onClose,
}: CallDisconnectConfirmationModalProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: disconnectCall, isPending } = useEndCall();
  const { track } = useTrackEvent();

  const handleDisconnect = async () => {
    try {
      room.exit();

      await disconnectCall({
        callId,
      });

      track(PostHogEvents.CALL_ENDED, {
        call_id: callId,
        room_id: room.roomId,
      });

      queryClient.invalidateQueries({
        queryKey: ["calls-active"],
      });
      // Close the call room
      window.close();

      // Redirect to the conversations page
      window.location.href = "/conversations";
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          onClose();
        }
      }}
    >
      <DialogContent className="w-full max-w-lg p-0" hideCloseButton>
        <div className="p-4">
          <DialogHeader>
            <DialogTitle className="mb-1 text-2xl font-semibold">
              Do you want to leave the call?
            </DialogTitle>
            <DialogDescription className="text-base">
              Ending the call will close this browser tab.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex flex-row items-center gap-4">
            <Button
              className="w-full text-lg font-bold text-slate-900"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
            >
              No
            </Button>
            <Button
              className="w-full text-lg font-bold text-white"
              variant="default"
              onClick={async () => {
                await handleDisconnect();
                onClose();
              }}
              disabled={isPending}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Yes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
